import React from 'react';
import { Activity, AlertTriangle, Users, Zap } from 'lucide-react';
import {
  useActiveUsers,
  useErrorVolume,
  useRequestVolume,
  useTotalUsers,
} from '../hooks';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/AnalyticsComponents/StatCard';
import { RequestsChart } from '../components/AdminDashboard/RequestsChart';
import { HourlyChart } from '../components/AdminDashboard/HourlyChart';
import { TopAgents } from '../components/AdminDashboard/TopAgents';
import { PlatformBreakdown } from '../components/AdminDashboard/PlatformBreakdown';

function formatDate(date) {
  return date.toISOString().split('T')[0];
}

const AdminDashboard = () => {
  const today = new Date();
  const lastMonth = new Date();
  const yesterday = new Date();

  yesterday.setDate(today.getDate() - 1);
  lastMonth.setMonth(today.getMonth() - 1);

  const monthStart = formatDate(lastMonth);
  const dayStart = formatDate(yesterday);
  const dayEnd = formatDate(today);

  const totalUsersQuery = useTotalUsers(monthStart, dayEnd);
  const activeUsersQuery = useActiveUsers(dayStart, dayEnd);
  const requestsQuery = useRequestVolume(dayStart, dayEnd);
  const errorsQuery = useErrorVolume(dayStart, dayEnd);

  const { data: totalUsers, isPending: totalUsersLoading } = totalUsersQuery;
  const { data: activeUsers, isPending: activeUsersLoading } = activeUsersQuery;
  const { data: requestsToday, isPending: requestsLoading } = requestsQuery;
  const { data: errorsToday, isPending: errorsLoading } = errorsQuery;

  // Stagger the load: fire the 4 lightweight KPI calls first, and only start
  // the 4 heavier chart calls once those settle, so the analytics backend
  // isn't hit with 8 concurrent requests on mount.
  const kpisSettled = [totalUsersQuery, activeUsersQuery, requestsQuery, errorsQuery].every(
    q => !q.isPending || q.isError,
  );

  const errorRate =
    requestsToday?.total_requests > 0
      ? ((errorsToday?.total_errors / requestsToday?.total_requests) * 100).toFixed(1)
      : '0.0';

  const formatValue = (value, loading) => {
    if (loading) return '—';
    if (value === undefined || value === null) return '0';
    return typeof value === 'number' ? value.toLocaleString() : value;
  };

  return (
    <div className="p-8 lg:p-10 space-y-8">
      <PageHeader
        title="Admin Panel"
        subtitle="Overview of your AgentCy Labs activity and platform health."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Users"
          value={formatValue(totalUsers?.total_users, totalUsersLoading)}
          sub="vs last month"
          icon={Users}
          color="#3B82F6"
        />
        <StatCard
          label="Active Today"
          value={formatValue(activeUsers?.total_users, activeUsersLoading)}
          sub="vs yesterday"
          icon={Activity}
          color="#10B981"
        />
        <StatCard
          label="Requests Today"
          value={formatValue(requestsToday?.total_requests, requestsLoading)}
          sub="vs yesterday"
          icon={Zap}
          color="#721b1f"
        />
        <StatCard
          label="Errors Today"
          value={formatValue(errorsToday?.total_errors, errorsLoading)}
          sub={`error rate: ${errorRate}%`}
          icon={AlertTriangle}
          color="#EF4444"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RequestsChart enabled={kpisSettled} />
        <HourlyChart enabled={kpisSettled} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopAgents enabled={kpisSettled} />
        <PlatformBreakdown enabled={kpisSettled} />
      </div>
    </div>
  );
};

export default AdminDashboard;
